import type { ConceptWorksheetFilterBody } from '@mathflat/domain/MakeWorksheet'
import type { TaggedProblem } from '@mathflat/domain/Problem'
import type { Student } from '@mathflat/domain/Student'
import { computed, makeObservable, observable, toJS } from 'mobx'

import type {
  ArithmeticLevel,
  MakeWorksheetBy,
  MakeWorksheetEqualityLevel,
  PreviousExclusionScope,
} from '@/types/makeWorksheet'

import { DTO } from '../DTO'
import type { MakeWorksheetFilterOption } from './MakeWorksheetFilterOption'

export class MakeWorksheetByArithmeticOption extends DTO {
  filterId = ''

  type: MakeWorksheetBy = 'ARITHMETIC'
  arithmeticLevel: ArithmeticLevel = 'ALL'
  conceptIdList: number[] = []
  excludedTopicIds: number[] = []
  excludedSubTopicIds: number[] = []
  problemCount = 50
  onlyAutoScorable = true
  excludePrevious = false
  previousExclusionScope?: PreviousExclusionScope
  studentIds?: Student['id'][] // 제외 기준이 STUDENT_ASSIGNED일 때 대상 학생 id 목록
  excludeOOC = true
  equalityLevel: MakeWorksheetEqualityLevel = null // 균등배분시 기준 - 선택 안하면 null
  minRate = 0
  maxRate = 100
  problemList:
    | {
        id: number
        tagTop: TaggedProblem['tagTop']
        baseWorkbookProblemId?: number | null
      }[]
    | null = null
  title?: string
  selectedConceptIdList: number[] = []
  selectedLittleChapterIdList: number[] = []
  source?: MakeWorksheetFilterOption['source']

  constructor(data?: Partial<MakeWorksheetByArithmeticOption>) {
    super()

    if (data) {
      Object.assign(this, data)
    }

    makeObservable(this, {
      filterId: observable,
      onlyAutoScorable: observable,
      excludePrevious: observable,
      previousExclusionScope: observable,
      studentIds: observable,
      excludeOOC: observable,
      type: observable,
      arithmeticLevel: observable,

      conceptIdList: observable,
      excludedTopicIds: observable,
      excludedSubTopicIds: observable,
      problemCount: observable,
      equalityLevel: observable,
      minRate: observable,
      maxRate: observable,
      problemList: observable,
      title: observable,
      selectedConceptIdList: observable,
      selectedLittleChapterIdList: observable,
      params: computed,
    })
  }

  get params(): ConceptWorksheetFilterBody {
    const params: ConceptWorksheetFilterBody = {
      arithmeticOnly: true,
      arithmeticLevel: this.arithmeticLevel,
      onlyAutoScorable: this.onlyAutoScorable,
      excludePrevious: this.excludePrevious,
      conceptIdList: toJS(this.conceptIdList),
      excludedTopicIds: this.excludedTopicIds,
      excludedSubTopicIds: this.excludedSubTopicIds,
      problemCount: this.problemCount,
      excludeOOC: this.excludeOOC,
      equalityLevel: this.equalityLevel,
      minRate: this.minRate,
      maxRate: this.maxRate,
      problemList: toJS(this.problemList) || null,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      source: this.source,
    }

    if (this.title) {
      params['title'] = this.title
    }
    if (this.filterId) {
      params['filterId'] = this.filterId
    }
    if (this.excludePrevious && this.previousExclusionScope) {
      params['previousExclusionScope'] = this.previousExclusionScope
      if (this.previousExclusionScope === 'STUDENT_ASSIGNED' && this.studentIds?.length) {
        params['studentIds'] = this.studentIds
      }
    }

    return params
  }
}
